import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { datadogRum } from "@datadog/browser-rum";

import { type InitialQuery, useInitialQuery } from "src/graphql";
import { HttpError } from "src/utils/fetcher";
import { HightouchRegionOptions } from "src/utils/regions";
import { isPersonalEmail } from "src/utils/user";
import { verifyWorkspace } from "src/utils/workspaces";

type WorkspaceError = {
  message: string;
  sessionWorkspace?: {
    id?: number;
    name?: string;
  };
};

type Workspace = InitialQuery["workspaces"][0] | undefined;

export type UserContextType = {
  error: string | undefined;
  user: InitialQuery["getCurrentUser"] | undefined;
  workspace: Workspace;
  // this is the organization that is the parent of the current
  // workspace, not the organization that an SSO user is a direct member of
  workspaceError: WorkspaceError | undefined;
  isOnboarding: boolean | undefined;
  cepConfigured: boolean;
  resources: Resources | undefined;
  slug: string | undefined | null;
  featureFlags: Record<string, boolean>;
  getWorkspaceIdFromSlug: (slug: string) => string | undefined;
  region: string;
  isCompanyRequired: boolean;
  isLoading: boolean;
  refetch: () => Promise<any>;
  isEmbedded: boolean;
  spaces: InitialQuery["spaces"] | undefined;
  workspaces: InitialQuery["workspaces"] | undefined;
};

const defaultUserContext: UserContextType = {
  error: undefined,
  user: undefined,
  workspace: undefined,
  isOnboarding: undefined,
  cepConfigured: false,
  resources: undefined,
  slug: undefined,
  workspaceError: undefined,
  featureFlags: {},
  getWorkspaceIdFromSlug: () => undefined,
  region: HightouchRegionOptions.AWS_US_EAST_1,
  isCompanyRequired: false,
  isLoading: true,
  refetch: () => Promise.resolve(),
  isEmbedded: true,
  spaces: undefined,
  workspaces: undefined,
};

export const UserContext = createContext<UserContextType>(defaultUserContext);
UserContext.displayName = "UserContext";

export const useUser = () => useContext(UserContext);

const COMPANY_REQUIRED_TIMESTAMP = "Mon Nov 29 2022 13:30:00 GMT-0500";

// The API answers 409 when another tab switched the session to a different workspace.
function isWorkspaceConflict(error: unknown): error is HttpError {
  return error instanceof HttpError && error.code === 409;
}

function toWorkspaceError(error: HttpError): WorkspaceError {
  return {
    message: error.message,
    sessionWorkspace: {
      id: error.body?.details?.session?.id,
      name: error.body?.details?.session?.name,
    },
  };
}

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: FC<UserProviderProps> = ({ children }) => {
  const [workspaceError, setWorkspaceError] = useState<
    WorkspaceError | undefined
  >();

  const { data, error, status, refetch } = useInitialQuery(
    {},
    {
      useErrorBoundary: false,
      meta: {
        isFastEndpoint: true,
      },
    },
  );

  // Only pass down a user if there wasn't an error. Most commonly, the error
  // will be a unauthenticated error if the user isn't logged in.
  // A workspace conflict keeps the cached user: the session is still signed in, and the banner asks for a reload.
  const user =
    data && (!error || isWorkspaceConflict(error))
      ? data.getCurrentUser
      : undefined;
  const workspaces = data?.workspaces;
  const spaces = data?.spaces;
  const workspace = data?.workspaces?.find(
    ({ id }) => id == user?.current_workspace_id,
  );

  const featureFlags = workspace?.feature_flags;

  const resources = useMemo(
    () =>
      data
        ? {
            destination: data.destinations[0],
            source: data.sources[0],
            model: data.models[0],
            sync: data.syncs[0],
            audience: data.audiences[0],
            parentModel: data.parentModels[0],
            decisionEngine: data.decisionEngines[0],
            canSequence: data.syncs.length > 1,
          }
        : undefined,
    [data],
  );

  // Must stay in sync with the required steps in useOnboardingSteps
  // (hooks/use-onboarding-steps.ts). Inlined here to avoid the extra
  // GraphQL queries that hook makes — those only need to run on the setup page.
  const isOnboarding = resources
    ? !resources.source?.id || !resources.destination?.id
    : undefined;

  const cepConfigured = (data?.cepConfig?.length ?? 0) > 0;

  const isCompanyRequired = Boolean(
    user &&
      user.created_at &&
      new Date(String(user.created_at)) >
        new Date(COMPANY_REQUIRED_TIMESTAMP) &&
      isPersonalEmail(user.email) &&
      !user.personalization?.company_name,
  );

  const getWorkspaceIdFromSlug = useCallback(
    (slug: string): string | undefined => {
      const workspace = workspaces?.find((w) => w.slug === slug);
      if (workspace) {
        return String(workspace.id);
      }

      return undefined;
    },
    [workspaces],
  );

  // Fast path for the bootstrap race: children's first react-query fetches run
  // in child effects, which fire before this provider's effect below — assign
  // during render so those first requests route to the workspace's regional
  // endpoint instead of falling back to the base host. The effect below stays
  // the reactive source of truth for every other workspace field change.
  if (
    window.Hightouch?.workspaceId !== workspace?.id ||
    window.Hightouch?.apiRegion !== workspace?.region
  ) {
    window.Hightouch = {
      ...window.Hightouch,
      apiRegion: workspace?.region,
      workspaceId: workspace?.id,
      regionConfig: workspace?.region_config,
      organizationId: workspace?.organization?.id,
    };
  }

  useEffect(() => {
    // Set the region and workspace id on the window.
    window.Hightouch = {
      ...window.Hightouch,
      apiRegion: workspace?.region,
      workspaceId: workspace?.id,
      regionConfig: workspace?.region_config,
      organizationId: workspace?.organization?.id,
    };

    // Check for workspace error.
    const checkForWorkspaceError = () => {
      // Note: We don't clear the workspace error on success
      //       because we always assume that atleast one network
      //       request had also failed so we don't have to whack-a-mole
      //       trying to find every case. User reload should clear it.
      verifyWorkspace().catch((e) => {
        // Primarily try to catch 409s.
        if (isWorkspaceConflict(e)) {
          setWorkspaceError(toWorkspaceError(e));
        }
        // Ignore other possible errors (ie. 500) since those
        // doesn't necessarily tell if there is something
        // wrong in the workspace.
      });
    };

    window.addEventListener("focus", checkForWorkspaceError);
    return () => {
      window.removeEventListener("focus", checkForWorkspaceError);
    };
  }, [workspace]);

  const userProviderTimingFired = useRef(false);

  useEffect(() => {
    if (data && resources && !userProviderTimingFired.current) {
      userProviderTimingFired.current = true;
      datadogRum.addTiming("user_provider_ready");
    }
  }, [data, resources]);

  const value = useMemo(
    () => ({
      workspaceError:
        workspaceError ??
        (isWorkspaceConflict(error) ? toWorkspaceError(error) : undefined),
      error: error?.message,
      user,
      workspace,
      featureFlags,
      resources,
      isOnboarding,
      cepConfigured,
      slug: workspace?.slug,
      region: workspace?.region ?? HightouchRegionOptions.AWS_US_EAST_1,
      getWorkspaceIdFromSlug,
      isCompanyRequired,
      isLoading: status === "loading",
      refetch,
      isEmbedded: user?.email === "guest",
      spaces,
      workspaces,
    }),
    [
      workspaceError,
      error,
      user,
      workspace,
      featureFlags,
      resources,
      isOnboarding,
      cepConfigured,
      getWorkspaceIdFromSlug,
      isCompanyRequired,
      status,
      refetch,
      spaces,
      workspaces,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export interface Resources {
  source: InitialQuery["sources"][0] | undefined;
  model: InitialQuery["models"][0] | undefined;
  destination: InitialQuery["destinations"][0] | undefined;
  sync: InitialQuery["syncs"][0] | undefined;
  audience: InitialQuery["audiences"][0] | undefined;
  parentModel: InitialQuery["parentModels"][0] | undefined;
  decisionEngine: InitialQuery["decisionEngines"][0] | undefined;
  canSequence: boolean;
}
